// lib/artifact-map-layout.js — ARTIFACT MAP LAYOUT
// ═══════════════════════════════════════════════════════════
// Pure layout functions for the Artifact Journey Map. No React,
// no side effects — just data transforms.
//
// Model:
//   - One global root node at the center ("Your thinking").
//   - Each session that produced artifacts becomes a WEDGE around
//     the center. Sessions are ordered by most recent artifact
//     first, going clockwise from the top.
//   - Artifacts within a wedge are placed along a radial ray from
//     center outward, oldest closest to center, newest furthest.
//   - Convergence nodes (multi-select synthesis threads — session_type
//     'artifact') get their own wedge and a distinct ring shape.
//   - Lineage from artifact_versions: versions are rendered as small
//     satellite nodes offset from their parent artifact.
// ═══════════════════════════════════════════════════════════

// Artifact type → color (mirrors features/artifact-library TYPE_COLORS)
export const ARTIFACT_TYPE_COLORS = {
  web:      '#c8a96e',
  code:     '#c46050',
  document: '#9b8fc4',
  image:    '#7a9e82',
  data:     '#6b8fa8',
  upload:   '#b8956a',
  svg:      '#7a9e82',
};

export const ROOT_COLOR = '#c8a96e';       // gold
export const CONVERGENCE_COLOR = '#9b8fc4'; // purple — synthesis threads
export const EDGE_COLOR = 'rgba(136, 136, 136, 0.2)';
export const VERSION_EDGE_COLOR = 'rgba(200, 169, 110, 0.35)';

export const VIEW_SIZE = 600;
export const CENTER = VIEW_SIZE / 2;
const ROOT_RADIUS = 16;
const NODE_RADIUS = 10;
const VERSION_RADIUS = 4;
const RAY_INNER = 78;
const RAY_OUTER = 232;
const VERSION_OFFSET = 14;
const WEDGE_PAD_ANGLE = 0.12;  // angular gap between adjacent wedges
const WEDGE_LABEL_R = 260;     // radial distance for session titles

// Shape artifacts + sessions + versions into a layout.
// Inputs:
//   artifacts — array from /api/user/artifacts
//   sessionsById — { [sessionId]: { id, session_type, title, created_at } }
//   versions — array from /api/user/artifact-versions (optional — if absent, no lineage edges)
// Returns { nodes, edges, sessions }
export function buildArtifactMap(artifacts, sessionsById = {}, versions = []) {
  const byArtifactId = new Map();
  (artifacts || []).forEach(a => byArtifactId.set(a.id, a));

  // Group artifacts by session_id. Orphan artifacts (no session_id or a
  // session_id we can't resolve) get bucketed under a synthetic '__orphan'
  // session so they still show up on the map.
  const grouped = new Map();
  (artifacts || []).forEach(a => {
    const key = a.session_id || '__orphan';
    if (!grouped.has(key)) grouped.set(key, []);
    grouped.get(key).push(a);
  });

  // Sort each session's artifacts by created_at ascending (oldest first —
  // they sit closer to the center).
  grouped.forEach(list => {
    list.sort((a, b) => tsOf(a.created_at) - tsOf(b.created_at));
  });

  // Sessions ordered by most recent artifact in the group, descending.
  const sessions = [...grouped.entries()].map(([sessionId, list]) => ({
    sessionId,
    artifacts: list,
    mostRecent: Math.max(...list.map(a => tsOf(a.created_at))),
    meta: sessionsById[sessionId] || null,
  }));
  sessions.sort((a, b) => b.mostRecent - a.mostRecent);

  if (sessions.length === 0) {
    return { nodes: [buildRoot()], edges: [], sessions: [], wedges: [] };
  }

  // Wedge angle per session, starting from the top (-PI/2) and going
  // clockwise.
  const wedgeSize = (Math.PI * 2) / sessions.length;
  const nodes = [buildRoot()];
  const edges = [];
  const wedges = [];

  sessions.forEach((group, sIdx) => {
    const wedgeCenter = -Math.PI / 2 + sIdx * wedgeSize;
    const count = group.artifacts.length;

    // Within a wedge, spread artifacts along a small angular band so
    // multi-artifact sessions don't render as a straight line. One-artifact
    // wedges get the exact wedge center.
    const usableSpread = Math.min(wedgeSize * 0.55, 0.55);

    // Wedge background bounds — slightly narrower than the full slice
    // so adjacent wedges have breathing room.
    const wedgeStart = wedgeCenter - (wedgeSize / 2) + WEDGE_PAD_ANGLE / 2;
    const wedgeEnd = wedgeCenter + (wedgeSize / 2) - WEDGE_PAD_ANGLE / 2;

    const isConvergence = group.meta?.session_type === 'artifact';
    const sessionLabel = labelForSession(group);

    const firstNodeIdx = nodes.length;

    group.artifacts.forEach((artifact, i) => {
      let angle;
      if (count === 1) {
        angle = wedgeCenter;
      } else {
        const t = i / (count - 1); // 0..1
        angle = wedgeCenter - usableSpread / 2 + t * usableSpread;
      }
      // Distance from center: oldest near RAY_INNER, newest near RAY_OUTER.
      // When a session only has one artifact, park it midway.
      const depthT = count === 1 ? 0.45 : (i / Math.max(count - 1, 1));
      const r = RAY_INNER + depthT * (RAY_OUTER - RAY_INNER);

      const node = {
        id: artifact.id,
        kind: 'artifact',
        artifact,
        x: CENTER + Math.cos(angle) * r,
        y: CENTER + Math.sin(angle) * r,
        radius: NODE_RADIUS,
        color: colorFor(artifact),
        sessionId: group.sessionId,
        isConvergence,
        angle,
        ray: r,
      };
      nodes.push(node);

      // Edge from the root to the first artifact in each session; after
      // that, edge from previous artifact in the same session (spine).
      if (i === 0) {
        edges.push({ from: nodes[0], to: node, kind: 'root' });
      } else {
        const prev = nodes[nodes.length - 2];
        edges.push({ from: prev, to: node, kind: 'spine' });
      }
    });

    wedges.push({
      sessionId: group.sessionId,
      label: sessionLabel,
      startAngle: wedgeStart,
      endAngle: wedgeEnd,
      midAngle: wedgeCenter,
      innerRadius: RAY_INNER - 10,
      outerRadius: RAY_OUTER + 10,
      labelX: CENTER + Math.cos(wedgeCenter) * WEDGE_LABEL_R,
      labelY: CENTER + Math.sin(wedgeCenter) * WEDGE_LABEL_R,
      isConvergence,
      count,
      nodeIds: nodes.slice(firstNodeIdx).map(n => n.id),
    });
  });

  // Lineage edges from artifact_versions. Each version row has
  // artifact_id + version_number. Versions fan out from their parent
  // as small satellite nodes.
  const versionsByArtifact = new Map();
  (versions || []).forEach(v => {
    if (!versionsByArtifact.has(v.artifact_id)) versionsByArtifact.set(v.artifact_id, []);
    versionsByArtifact.get(v.artifact_id).push(v);
  });

  versionsByArtifact.forEach((list, artifactId) => {
    const parentNode = nodes.find(n => n.id === artifactId);
    if (!parentNode) return;
    list.sort((a, b) => (a.version_number || 0) - (b.version_number || 0));

    // Fan versions outward from the parent at a small angular spread
    // perpendicular to the parent's ray.
    const parentAngle = Math.atan2(parentNode.y - CENTER, parentNode.x - CENTER);
    const perp = parentAngle + Math.PI / 2;
    list.forEach((v, i) => {
      const t = list.length === 1 ? 0 : (i - (list.length - 1) / 2);
      const dx = Math.cos(perp) * (VERSION_OFFSET * (1 + i * 0.6));
      const dy = Math.sin(perp) * (VERSION_OFFSET * (1 + i * 0.6));
      const node = {
        id: `v-${v.id}`,
        kind: 'version',
        version: v,
        parentArtifactId: artifactId,
        x: parentNode.x + dx + t * 2,
        y: parentNode.y + dy + t * 2,
        radius: VERSION_RADIUS,
        color: VERSION_EDGE_COLOR,
      };
      nodes.push(node);
      edges.push({ from: parentNode, to: node, kind: 'version' });
    });
  });

  return { nodes, edges, sessions, wedges };
}

// Build an SVG `d` string for an annulus sector: inner arc + outer arc.
export function buildWedgePath(wedge) {
  const { startAngle, endAngle, innerRadius, outerRadius } = wedge;
  const x1 = CENTER + Math.cos(startAngle) * outerRadius;
  const y1 = CENTER + Math.sin(startAngle) * outerRadius;
  const x2 = CENTER + Math.cos(endAngle) * outerRadius;
  const y2 = CENTER + Math.sin(endAngle) * outerRadius;
  const x3 = CENTER + Math.cos(endAngle) * innerRadius;
  const y3 = CENTER + Math.sin(endAngle) * innerRadius;
  const x4 = CENTER + Math.cos(startAngle) * innerRadius;
  const y4 = CENTER + Math.sin(startAngle) * innerRadius;
  const largeArc = (endAngle - startAngle) > Math.PI ? 1 : 0;
  return `M ${x1} ${y1} A ${outerRadius} ${outerRadius} 0 ${largeArc} 1 ${x2} ${y2} L ${x3} ${y3} A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${x4} ${y4} Z`;
}

// Short label for a session. Prefers the session title; falls back to
// a relative date like "Apr 14". Orphan artifacts (no session row)
// get a neutral marker.
function labelForSession(group) {
  const meta = group.meta;
  if (group.sessionId === '__orphan') return 'unassigned';
  if (meta?.title) return truncate(meta.title, 18);
  const createdAt = meta?.created_at || group.artifacts[0]?.created_at;
  if (createdAt) {
    try {
      return new Date(createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch { /* ignore */ }
  }
  return 'session';
}

export function truncate(str, n) {
  if (!str) return '';
  if (str.length <= n) return str;
  return str.slice(0, n - 1).trimEnd() + '…';
}

function buildRoot() {
  return {
    id: '__root',
    kind: 'root',
    x: CENTER,
    y: CENTER,
    radius: ROOT_RADIUS,
    color: ROOT_COLOR,
  };
}

function tsOf(iso) {
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  return Number.isFinite(t) ? t : 0;
}

function colorFor(artifact) {
  const key = (artifact.artifact_type || '').toLowerCase();
  return ARTIFACT_TYPE_COLORS[key] || '#888';
}
